/**
 * Authentication Helper Functions
 * Utility functions for auth-related operations
 */

import { UserProfileResponse, UserAliasRequest, MappedRole } from '../api/types';
import { getAuthType } from './authStorage';

// ============================================================================
// Role Extraction
// ============================================================================

/**
 * Extract roles from mapped roles structure
 */
export function getRoles(mappedRoles: MappedRole[]): string[] {
  const roles: string[] = [];
  
  for (const mappedRole of mappedRoles) {
    const mappings = mappedRole.mappingsRespList;
    for (const mapping of mappings) {
      roles.push(mapping.role);
    }
  }
  
  return roles;
}

// ============================================================================
// User Alias Request Builder
// ============================================================================

/**
 * Extract roles from user profile (MSID auth)
 */
function extractRolesFromProfile(userProfile: UserProfileResponse['decodedIdToken']): string[] {
  const rolesList: string[] = [];
  const roles = userProfile.roles;
  
  if (!roles) return rolesList;
  
  for (const role of roles) {
    if (role.startsWith('CN=RX')) {
      const trimmedRole = role.substring(
        role.indexOf('=') + 1,
        role.indexOf(',')
      );
      rolesList.push(trimmedRole);
    }
  }
  
  return rolesList;
}

/**
 * Build user alias request from user profile and abilities
 */
export function buildUserAliasRequest(
  userProfile: UserProfileResponse['decodedIdToken'],
  abilities: string[]
): UserAliasRequest {
  const authType = getAuthType();
  
  // Determine user ID and roles based on auth type
  let userId: string;
  let rolesList: string[];
  
  if (authType === 'msid') {
    userId = userProfile.msid || userProfile.preferred_username || userProfile.sub;
    rolesList = extractRolesFromProfile(userProfile);
  } else {
    // OHID or other auth types
    userId = userProfile.preferred_username || userProfile.sub;
    rolesList = abilities;
  }
  
  return {
    loginType: authType,
    userId,
    firstName: userProfile.given_name,
    lastName: userProfile.family_name,
    emailAddress: userProfile.email,
    sub: userProfile.sub,
    roles: rolesList,
  };
}

// ============================================================================
// Feature Flags
// ============================================================================

/**
 * Check whether the auth-proxy (authpass) login flow is enabled
 */
export function isAuthPassEnabled(): boolean {
  const environments = (window as any)['environments'];
  return environments?.authpassEnabled === true;
}

// ============================================================================
// URL Utilities
// ============================================================================

/**
 * Get the current host URI
 */
export function getHostUri(): string {
  // Sanitization to prevent open redirect vulnerabilities  
  const protocol = window.location.protocol;
  const host = window.location.host;
  
  return `${protocol}//${host}`;
}
